/**
 * ProductCatalog — user's product keywords (+ negative keywords) that drive
 * the "Match de Produto" tags on the Gov Kanban, alert sensitivity settings
 * and the "Testar Alcance Histórico" USASpending reach test.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Package, Plus, X, MapPin, Bell, Sparkles, TrendingUp,
  Target, Ban,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

function ChipInput({ chips, onChange, placeholder, color, testid }) {
  const [val, setVal] = useState("");
  const add = () => {
    const v = val.trim();
    if (v.length >= 3 && !chips.includes(v)) onChange([...chips, v]);
    setVal("");
  };
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder} data-testid={testid}
          className="al-input h-9 text-[12.5px] flex-1" />
        <button type="button" onClick={add} data-testid={`${testid}-add`}
          className="h-9 w-9 rounded-lg border border-[var(--al-border)] inline-flex items-center justify-center hover:bg-[var(--al-card)]">
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {chips.map((c) => (
          <span key={c} className={`text-[11.5px] px-2 py-1 rounded-full border inline-flex items-center gap-1.5 ${color}`}>
            {c}
            <button type="button" onClick={() => onChange(chips.filter((x) => x !== c))}
              className="hover:opacity-70"><X className="w-3 h-3" /></button>
          </span>
        ))}
        {chips.length === 0 && (
          <span className="text-[11.5px] text-[var(--al-text-muted)] italic">Nenhuma palavra-chave ainda</span>
        )}
      </div>
    </div>
  );
}

export default function ProductCatalog() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [cat, setCat] = useState(null);
  const [saving, setSaving] = useState(false);
  const [matching, setMatching] = useState(false);
  const [reach, setReach] = useState(null);
  const [reachLoading, setReachLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/product-catalog`).then(({ data }) => setCat(data))
      .catch(() => toast.error("Falha ao carregar catálogo"));
  }, []);

  if (!cat) {
    return (
      <div className="flex min-h-screen bg-[var(--al-bg)]"><Sidebar />
        <main className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></main>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/product-catalog`, cat);
      setCat({ ...cat, ...data.catalog });
      toast.success(data.hq_geocoded
        ? "Catálogo salvo — sede geocodificada 📍"
        : "Catálogo salvo");
    } catch (_e) { toast.error("Falha ao salvar"); }
    finally { setSaving(false); }
  };

  const matchAll = async () => {
    setMatching(true);
    try {
      const { data } = await axios.post(`${API}/product-catalog/match-all`);
      if (data.ok) {
        toast.success(`Matching aplicado: ${data.matched} match(es) em ${data.scanned} licitações (${data.perfect} perfeitos)`);
      } else {
        toast.info("Cadastre palavras-chave e salve antes de aplicar");
      }
    } catch (_e) { toast.error("Falha no matching"); }
    finally { setMatching(false); }
  };

  const testReach = async () => {
    setReachLoading(true);
    try {
      const { data } = await axios.post(`${API}/product-catalog/test-reach`, {
        keywords: cat.keywords, days: 30,
      });
      if (data.ok) setReach(data);
      else toast.info("Cadastre pelo menos 1 palavra-chave (mín. 3 letras)");
    } catch (_e) { toast.error("Falha na consulta USASpending"); }
    finally { setReachLoading(false); }
  };

  return (
    <div className="flex min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]" data-testid="product-catalog-page">
      <Sidebar />
      <main className="flex-1 min-w-0 px-6 py-6 lg:px-10">
        <div className="max-w-4xl mx-auto space-y-6">
          <header>
            <h1 className="text-[22px] font-extrabold inline-flex items-center gap-2">
              <Package className="w-6 h-6 text-[#C9A24E]" />
              {isPt ? "Catálogo de Produtos" : "Product Catalog"}
            </h1>
            <p className="text-[12.5px] text-[var(--al-text-muted)] mt-1">
              {isPt
                ? "Cadastre o que você vende — a Claire compara com os editais e marca 'Match de Produto' no Kanban de Obras"
                : "Register what you sell — Claire matches it against solicitations and tags the Gov Kanban"}
            </p>
          </header>

          {/* Test reach — hero position (conversion driver) */}
          <div className="al-card p-5 border-2 border-[#C9A24E]/40" data-testid="pc-reach-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-[14px] font-bold inline-flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#C9A24E]" />
                  {isPt ? "Testar Alcance Histórico" : "Test Historical Reach"}
                </h3>
                <p className="text-[12px] text-[var(--al-text-muted)] mt-0.5">
                  {isPt ? "Quanto o governo comprou dos seus produtos nos últimos 30 dias?" : "How much did the government buy of your products in the last 30 days?"}
                </p>
              </div>
              <button type="button" data-testid="pc-reach-btn" onClick={testReach}
                disabled={reachLoading || cat.keywords.length === 0}
                className="h-10 px-4 rounded-lg bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233] text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
                {reachLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {isPt ? "Testar agora" : "Test now"}
              </button>
            </div>
            {reach && (
              <div className="mt-4" data-testid="pc-reach-result">
                <p className="text-[15px]">
                  {isPt ? "Encontramos " : "We found "}
                  <b className="text-[#C9A24E]">{reach.contracts_found} {isPt ? "contratos" : "contracts"}</b>
                  {isPt ? " nos últimos 30 dias que dão match com seus produtos, totalizando " : " in the last 30 days matching your products, totaling "}
                  <b className="text-[#C9A24E]">{fmtMoney(reach.total_value_top100)}</b>
                  {isPt ? " (top 100)." : " (top 100)."}
                </p>
                <div className="grid sm:grid-cols-3 gap-2 mt-3">
                  {(reach.top_contracts || []).map((c, i) => (
                    <div key={`tc-${i}`} className="al-card p-3 text-[11.5px]" data-testid={`pc-reach-top-${i}`}>
                      <div className="font-extrabold text-[13px] text-[#1B3A8A] mb-1">{fmtMoney(c.amount)}</div>
                      <div className="line-clamp-2 leading-snug">{c.title}</div>
                      <div className="text-[var(--al-text-muted)] mt-1 truncate">{c.agency} · {c.state}</div>
                      {c.locked ? (
                        <div className="mt-2 rounded-lg border border-[#C9A24E]/40 bg-[#C9A24E]/5 p-2"
                          data-testid={`pc-reach-locked-${i}`}>
                          <div className="select-none" style={{ filter: "blur(5px)" }} aria-hidden>
                            <div>🏆 Winner Construction LLC</div>
                            <div>✉️ contact@winner.com</div>
                            <div>🔗 usaspending.gov/award/xxxx</div>
                          </div>
                          <div className="text-[10.5px] font-bold mt-1.5 inline-flex items-center gap-1">
                            🔒 {isPt ? "Disponível apenas no plano Ultimate" : "Ultimate plan only"}
                          </div>
                          <a href="/billing" data-testid={`pc-reach-upgrade-${i}`}
                            className="block text-center mt-1.5 h-7 leading-7 rounded-md bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233] text-[10.5px] font-extrabold">
                            {isPt ? "Fazer upgrade ⚡" : "Upgrade ⚡"}
                          </a>
                        </div>
                      ) : (
                        <div className="mt-2 text-[11px] space-y-0.5">
                          {c.recipient && <div className="font-semibold truncate">🏆 {c.recipient}</div>}
                          {c.contract_url && (
                            <a href={c.contract_url} target="_blank" rel="noopener noreferrer"
                              className="text-[#3B82F6] hover:underline inline-block">
                              🔗 {isPt ? "Ver edital no USASpending" : "View on USASpending"}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div className="al-card p-5">
              <h3 className="text-[13px] font-bold mb-1 inline-flex items-center gap-1.5">
                <Target className="w-4 h-4 text-emerald-500" /> {isPt ? "Produtos que você vende" : "Products you sell"}
              </h3>
              <p className="text-[11.5px] text-[var(--al-text-muted)] mb-3">
                {isPt ? 'Ex: "PVC pipes", "cement", "steel beams" (em inglês — editais dos EUA)' : 'E.g. "PVC pipes", "cement", "steel beams"'}
              </p>
              <ChipInput chips={cat.keywords}
                onChange={(v) => setCat({ ...cat, keywords: v })}
                placeholder={isPt ? "Adicionar produto..." : "Add product..."}
                color="bg-emerald-500/10 border-emerald-500/40 text-emerald-600"
                testid="pc-kw-input" />
            </div>
            <div className="al-card p-5">
              <h3 className="text-[13px] font-bold mb-1 inline-flex items-center gap-1.5">
                <Ban className="w-4 h-4 text-rose-500" /> {isPt ? "Palavras-chave negativas" : "Negative keywords"}
              </h3>
              <p className="text-[11.5px] text-[var(--al-text-muted)] mb-3">
                {isPt ? 'Ignora falsos positivos. Ex: match em "steel", mas ignorar "steel cabinets"' : 'Filters false positives, e.g. match "steel" but skip "steel cabinets"'}
              </p>
              <ChipInput chips={cat.negative_keywords}
                onChange={(v) => setCat({ ...cat, negative_keywords: v })}
                placeholder={isPt ? "Adicionar exclusão..." : "Add exclusion..."}
                color="bg-rose-500/10 border-rose-500/40 text-rose-500"
                testid="pc-neg-input" />
            </div>
          </div>

          <div className="al-card p-5">
            <h3 className="text-[13px] font-bold mb-1 inline-flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-[#3B82F6]" /> {isPt ? "Sede da empresa (cálculo de frete)" : "Company HQ (freight distance)"}
            </h3>
            <p className="text-[11.5px] text-[var(--al-text-muted)] mb-2">
              {isPt ? "A distância da sede até a obra aparece nos cards com Match de Produto" : "Distance from HQ to the job site is shown on matched cards"}
            </p>
            <input value={cat.hq_address}
              onChange={(e) => setCat({ ...cat, hq_address: e.target.value })}
              placeholder={isPt ? "Ex: 123 Main St, Orlando, FL" : "E.g. 123 Main St, Orlando, FL"}
              data-testid="pc-hq-input"
              className="al-input h-10 text-[12.5px] w-full" />
            {cat.hq_lat != null && (
              <p className="text-[10.5px] text-emerald-600 mt-1.5">📍 {isPt ? "Geocodificada" : "Geocoded"}: {cat.hq_lat?.toFixed(4)}, {cat.hq_lng?.toFixed(4)}</p>
            )}
          </div>

          <div className="al-card p-5" data-testid="pc-sensitivity">
            <h3 className="text-[13px] font-bold mb-3 inline-flex items-center gap-1.5">
              <Bell className="w-4 h-4 text-amber-500" /> {isPt ? "Filtro de Sensibilidade dos Alertas" : "Alert Sensitivity Filter"}
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={cat.instant_enabled} data-testid="pc-instant-toggle"
                  onChange={(e) => setCat({ ...cat, instant_enabled: e.target.checked })}
                  className="accent-[#C9A24E] w-4 h-4 mt-0.5" />
                <span className="text-[12.5px]">
                  <b>{isPt ? "Instantâneo" : "Instant"}</b><br />
                  <span className="text-[11px] text-[var(--al-text-muted)]">
                    {isPt ? "Só para Matches Perfeitos (produto + valor + distância)" : "Perfect matches only"}
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={cat.digest_enabled} data-testid="pc-digest-toggle"
                  onChange={(e) => setCat({ ...cat, digest_enabled: e.target.checked })}
                  className="accent-[#C9A24E] w-4 h-4 mt-0.5" />
                <span className="text-[12.5px]">
                  <b>{isPt ? "Resumo diário" : "Daily digest"}</b><br />
                  <span className="text-[11px] text-[var(--al-text-muted)]">
                    {isPt ? "Tudo que é relevante mas não urgente, toda manhã" : "Everything relevant, every morning"}
                  </span>
                </span>
              </label>
              <div>
                <label className="text-[11px] font-bold text-[var(--al-text-muted)] uppercase">{isPt ? "Valor mínimo p/ instantâneo (US$)" : "Min value for instant (US$)"}</label>
                <input type="number" value={cat.instant_min_value} data-testid="pc-min-value"
                  onChange={(e) => setCat({ ...cat, instant_min_value: Number(e.target.value) })}
                  className="al-input h-9 text-[12.5px] w-full mt-1" />
              </div>
              <div>
                <label className="text-[11px] font-bold text-[var(--al-text-muted)] uppercase">{isPt ? "Distância máxima (milhas)" : "Max distance (miles)"}</label>
                <input type="number" value={cat.instant_max_distance_mi} data-testid="pc-max-dist"
                  onChange={(e) => setCat({ ...cat, instant_max_distance_mi: Number(e.target.value) })}
                  className="al-input h-9 text-[12.5px] w-full mt-1" />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button type="button" data-testid="pc-save-btn" onClick={save} disabled={saving}
              className="h-11 px-5 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[13px] font-bold inline-flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
              {isPt ? "Salvar catálogo" : "Save catalog"}
            </button>
            <button type="button" data-testid="pc-match-btn" onClick={matchAll} disabled={matching}
              className="h-11 px-5 rounded-lg border border-[#C9A24E] text-[#C9A24E] hover:bg-[#C9A24E]/10 text-[13px] font-bold inline-flex items-center gap-2 disabled:opacity-60">
              {matching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {isPt ? "Aplicar matching no Kanban agora" : "Apply matching now"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
